import React, { useState, useRef } from 'react';
import { Modal, Button, Form, Alert, Table, Spinner, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { childAPI } from '../utils/api';
import type { BulkImportRequest, BulkImportResponse } from '../types/child';

interface BulkImportModalProps {
  show: boolean;
  onHide: () => void;
  nurseryId: string;
  onSuccess: () => void;
}

interface RawCSVRow {
  [key: string]: string;
}

const BulkImportModal: React.FC<BulkImportModalProps> = ({ show, onHide, nurseryId, onSuccess }) => {
  const { t } = useTranslation('components', { keyPrefix: 'bulkImportModal' });
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<'strict' | 'partial'>('strict');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkImportResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const parseOptionalInteger = (value?: string) => {
    const trimmed = value?.trim();
    if (!trimmed) return undefined;

    const parsed = Number(trimmed);
    return Number.isInteger(parsed) ? parsed : undefined;
  };

  const cleanOptionalValue = (value?: string) => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setError(null);
      setResult(null);
    }
  };

  const parseCSV = (text: string): BulkImportRequest['children'] => {
    const lines = text.split(/\r?\n/);
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const data: BulkImportRequest['children'] = [];

    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      
      const values = lines[i].split(',').map(v => v.trim());
      const entry: RawCSVRow = {};
      
      headers.forEach((header, index) => {
        if (header) {
          entry[header] = values[index] || '';
        }
      });

      // Basic mapping for common headers
      const mappedEntry = {
        name: cleanOptionalValue(entry.name || entry.child_name) || '',
        phone: cleanOptionalValue(entry.phone),
        email: cleanOptionalValue(entry.email),
        classroom_id: parseOptionalInteger(entry.classroom_id),
        schedule_id: parseOptionalInteger(entry.schedule_id),
        date_of_birth: cleanOptionalValue(entry.date_of_birth || entry.dob),
        gender: cleanOptionalValue(entry.gender),
        enrollment_date: cleanOptionalValue(entry.enrollment_date),
        allergies: cleanOptionalValue(entry.allergies),
        emergency_contact: cleanOptionalValue(entry.emergency_contact),
        status: cleanOptionalValue(entry.status)
      };

      data.push(mappedEntry);
    }

    return data;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError(t('pleaseSelectCsv'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResult(null);

      const text = await file.text();
      const children = parseCSV(text);

      if (children.length === 0) {
        setError(t('csvEmptyOrInvalid'));
        setLoading(false);
        return;
      }

      if (children.length > 500) {
        setError(t('maxRowsExceeded'));
        setLoading(false);
        return;
      }

      // Validate fields required by the backend before submitting in strict mode.
      const invalidRows = children.filter(child => !child.name || !child.classroom_id);
      if (invalidRows.length > 0 && mode === 'strict') {
        setError(t('missingRequiredFields', { count: invalidRows.length }));
        setLoading(false);
        return;
      }

      const request: BulkImportRequest = { children };
      const response = await childAPI.bulkImport(nurseryId, request, mode);
      
      setResult(response);
      if (response.success && response.summary && response.summary.created > 0) {
        onSuccess();
      }
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data) {
        const errorData = err.response.data as BulkImportResponse;
        if (errorData.errors && errorData.errors.length > 0) {
          setResult(errorData);
          return;
        }
        setError(errorData.message || t('importFailed'));
      } else {
        const errorMessage = err instanceof Error ? err.message : t('processFailed');
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setFile(null);
    setMode('strict');
    setLoading(false);
    setError(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const downloadTemplate = () => {
    const headers = ['name', 'classroom_id', 'phone', 'email', 'schedule_id', 'date_of_birth', 'gender', 'enrollment_date', 'allergies', 'emergency_contact', 'status'];
    const csvContent = headers.join(',') + '\n';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'child_import_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    resetModal();
    onHide();
  };

  const getResultAlertVariant = (importResult: BulkImportResponse) => {
    if (!importResult.success) return 'danger';
    if (!importResult.summary || importResult.summary.failed > 0 || importResult.summary.created === 0) return 'warning';
    return 'success';
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{t('title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!result ? (
          <Form onSubmit={handleSubmit}>
            <Alert variant="info" className="d-flex justify-content-between align-items-center">
              <div>
                <strong>{t('requiredFields')}</strong> {t('requiredFieldsDetail')}
              </div>
              <Button variant="outline-primary" size="sm" onClick={downloadTemplate}>
                {t('downloadTemplate')}
              </Button>
            </Alert>

            {error && <Alert variant="danger">{error}</Alert>}

            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('selectCsvFile')}</Form.Label>
                  <Form.Control 
                    type="file" 
                    accept=".csv" 
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('importMode')}</Form.Label>
                  <Form.Select 
                    value={mode} 
                    onChange={(e) => setMode(e.target.value as 'strict' | 'partial')}
                  >
                    <option value="strict">{t('strict')}</option>
                    <option value="partial">{t('partial')}</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button variant="secondary" onClick={handleClose} disabled={loading}>
                {t('cancel')}
              </Button>
              <Button variant="primary" type="submit" disabled={loading || !file}>
                {loading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                {t('uploadAndImport')}
              </Button>
            </div>
          </Form>
        ) : (
          <div>
            <Alert variant={getResultAlertVariant(result)}>
              <h5>{result.message || t('importSummary')}</h5>
              {result.summary && (
                <p className="mb-0">
                  {t('summaryLine', { total: result.summary.total, created: result.summary.created, failed: result.summary.failed })}
                </p>
              )}
            </Alert>

            {result.success && result.summary && result.summary.created > 0 && result.created && result.created.length > 0 && (
              <div className="mt-4">
                <h6>{t('createdRows')}</h6>
                <div className="table-responsive" style={{ maxHeight: '300px' }}>
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>{t('row')}</th>
                        <th>{t('childName')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.created.map((created, idx) => (
                        <tr key={idx}>
                          <td>{created.row}</td>
                          <td>{created.name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}

            {result.errors && result.errors.length > 0 && (
              <div className="mt-4">
                <h6>{t('errors')}</h6>
                <div className="table-responsive" style={{ maxHeight: '300px' }}>
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>{t('row')}</th>
                        <th>{t('childName')}</th>
                        <th>{t('errorMessages')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      { (result.errors ?? []).map((err, idx) => (
                        <tr key={idx}>
                          <td>{err.row}</td>
                          <td>{err.name || '-'}</td>
                          <td>
                            <ul className="mb-0 ps-3 text-danger small">
                              {err.errors.map((msg, mIdx) => (
                                <li key={mIdx}>{msg}</li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end mt-4 gap-2">
              <Button variant="outline-secondary" onClick={handleReset}>
                {t('uploadAgain')}
              </Button>
              <Button variant="primary" onClick={handleClose}>
                {t('close')}
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default BulkImportModal;
